// Многострочное поле: причина, основание, пояснение — то, что человек пишет словами.
//
// Заведено 25.08.2026. До него текстовых областей в продукте было ТРИ, все набраны своей
// вёрсткой и все разные: у одной скругление 8 и подсветка фокуса синим, у двух других
// скругление, которого в теме нет вовсе (то есть прямой угол), и подсветка серым. Стояли они
// в трёх местах, где человек объясняет решение о деньгах: причина досрочного отъезда,
// основание окончательного расчёта и вставка сумм из книги заказчика.
//
// Близнец `TextField` и `SelectField`: тот же порядок частей, та же грамматика ошибки — форма
// обязана читаться как одна форма, а не как склейка полей.

import type { ReactNode } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import * as Hint from '@/shared/ui/hint';
import * as Label from '@/shared/ui/label';
import { cn } from '@/utils/cn';

export interface TextAreaProps {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  /**
   * Уход из поля. Нужен там, где текст сохраняется сам: сохранять на каждую букву значит
   * слать запрос на нажатие, а по кнопке — завести кнопку, которую забудут нажать.
   */
  onBlur?: () => void;
  placeholder?: string;
  /** Сколько строк видно без прокрутки. По умолчанию три: причина обычно в одну-две фразы. */
  rows?: number;
  maxLength?: number;
  /** Что не так с введённым: причина и исправление, а не «неверное значение». */
  error?: string;
  /** Подсказка, когда ошибки нет: зачем поле и что в нём ждут. */
  hint?: ReactNode;
  /** Моноширинный ввод — для вставки столбца сумм, где важно совпадение разрядов. */
  monospace?: boolean;
}

export function TextArea({
  id,
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  rows = 3,
  maxLength,
  error,
  hint,
  monospace,
}: TextAreaProps) {
  return (
    <div className='flex flex-col gap-1'>
      <Label.Root htmlFor={id}>
        {label}
        {required === true && <Label.Asterisk />}
      </Label.Root>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        aria-invalid={error !== undefined}
        className={cn(
          'w-full resize-y rounded-lg bg-bg-white-0 px-2.5 py-2 text-paragraph-sm text-text-strong-950',
          'ring-1 ring-inset ring-stroke-soft-200 transition duration-200 ease-out',
          'placeholder:text-text-soft-400',
          'focus:outline-none focus:ring-2 focus:ring-stroke-strong-950',
          error !== undefined && 'ring-error-base focus:ring-error-base',
          monospace === true && 'font-mono tabular-nums',
        )}
      />
      {error !== undefined ? (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {error}
        </Hint.Root>
      ) : (
        hint !== undefined && <Hint.Root>{hint}</Hint.Root>
      )}
    </div>
  );
}
